import { useCallback, useState } from "react";

import { type ModalProps, useToggleState } from "./useToggleState";

export interface PhoneVerificationGate {
    phoneModalProps: ModalProps;
    openPhoneVerificationModal: () => void;
    /**
     * True while a check is in flight. Callers MUST disable every control that can change
     * enrollment, or a second click can land mid-check and both programs end up selected.
     */
    isCheckingPhone: boolean;
    /** Resolves `false` — and opens the modal — when enrollment must be blocked. */
    requireVerifiedPhone: (enrolling: boolean) => Promise<boolean>;
}

/**
 * Shared phone-verification gate for Lens+/exclusive enrollment. The status is checked per
 * click, so a creator who leaves to verify in the Snapchat app only has to toggle again.
 * Short-circuits before the check when the COF gate is off, keeping it free until ramp.
 *
 * Owned by the surface that renders the enrollment controls, since blocking them for the
 * duration of the check is what keeps Lens+ and Top Performer mutually exclusive.
 */
export const usePhoneVerificationGate = (
    required: boolean,
    checkHasVerifiedPhone: () => Promise<boolean>,
): PhoneVerificationGate => {
    const [phoneModalProps, openPhoneVerificationModal] = useToggleState();
    const [isCheckingPhone, setIsCheckingPhone] = useState(false);

    const requireVerifiedPhone = useCallback(
        async (enrolling: boolean): Promise<boolean> => {
            if (!enrolling || !required) {
                return true;
            }
            setIsCheckingPhone(true);
            try {
                if (await checkHasVerifiedPhone()) {
                    return true;
                }
                openPhoneVerificationModal();
                return false;
            } finally {
                setIsCheckingPhone(false);
            }
        },
        [required, checkHasVerifiedPhone, openPhoneVerificationModal],
    );

    return { phoneModalProps, openPhoneVerificationModal, isCheckingPhone, requireVerifiedPhone };
};
